import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Download, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Badge } from '@/shared/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert';
import {
  useDisableTotp,
  useMfaStatus,
  useRotateRecoveryCodes,
  useTotpConfirm,
  useTotpSetup,
} from '@/app/auth/services/mfa';
import { formatTotpManualKey } from '@/app/auth/utils/loginChallenge';
import type { TotpSetup } from '@/shared/types/mfaTypes';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';

type DialogView = 'overview' | 'setup' | 'recovery' | 'disable' | 'rotate';

interface MfaSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const downloadRecoveryCodes = (codes: string[]) => {
  const blob = new Blob(
    [
      'Orbius recovery codes\n',
      'Store these in a safe place. Each code can be used once.\n\n',
      codes.join('\n'),
      '\n',
    ],
    { type: 'text/plain' },
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'orbius-recovery-codes.txt';
  link.click();
  URL.revokeObjectURL(url);
};

const copyText = async (text: string, label: string) => {
  await navigator.clipboard.writeText(text);
  showSuccess(`${label} copied to clipboard`);
};

const MfaSettingsDialog: React.FC<MfaSettingsDialogProps> = ({ isOpen, onClose }) => {
  const [view, setView] = useState<DialogView>('overview');
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [setupCode, setSetupCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [reauthCode, setReauthCode] = useState('');

  const mfaStatus = useMfaStatus(isOpen);
  const totpSetup = useTotpSetup();
  const totpConfirm = useTotpConfirm();
  const rotateRecovery = useRotateRecoveryCodes();
  const disableTotp = useDisableTotp();

  const totpEnabled = mfaStatus.data?.enabled === true && mfaStatus.data.type === 'TOTP';

  useEffect(() => {
    if (!isOpen) {
      setView('overview');
      setSetup(null);
      setSetupCode('');
      setRecoveryCodes([]);
      setPassword('');
      setShowPassword(false);
      setReauthCode('');
    }
  }, [isOpen]);

  const handleStartSetup = async () => {
    try {
      const result = await totpSetup.mutateAsync();
      setSetup(result);
      setSetupCode('');
      setView('setup');
    } catch (error) {
      showErrorWithApiError(error, ERROR.MFA.SETUP_FAILED);
    }
  };

  const handleConfirmSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (setupCode.length !== 6) {
      showError(ERROR.OTP.MISSING_OTP);
      return;
    }
    try {
      const result = await totpConfirm.mutateAsync(setupCode);
      setRecoveryCodes(result.recoveryCodes ?? []);
      setView('recovery');
      showSuccess(SUCCESS.MFA.ENABLED);
    } catch (error) {
      showErrorWithApiError(error, ERROR.MFA.CONFIRM_FAILED);
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    const isTotp = /^\d{6}$/.test(reauthCode);
    const isRecovery = reauthCode.replace(/-/g, '').length >= 8;
    if (!password || (!isTotp && !isRecovery)) {
      showError(ERROR.MFA.REAUTH_REQUIRED);
      return;
    }
    try {
      await disableTotp.mutateAsync({
        password,
        code: reauthCode,
        factor: isTotp ? 'TOTP' : 'RECOVERY',
      });
      showSuccess(SUCCESS.MFA.DISABLED);
      setView('overview');
      setPassword('');
      setReauthCode('');
    } catch (error) {
      showErrorWithApiError(error, ERROR.MFA.DISABLE_FAILED);
    }
  };

  const handleRotate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reauthCode.length !== 6) {
      showError(ERROR.OTP.MISSING_OTP);
      return;
    }
    try {
      const result = await rotateRecovery.mutateAsync(reauthCode);
      setRecoveryCodes(result.recoveryCodes ?? []);
      setReauthCode('');
      setView('recovery');
      showSuccess(SUCCESS.MFA.RECOVERY_ROTATED);
    } catch (error) {
      showErrorWithApiError(error, ERROR.MFA.ROTATE_FAILED);
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium">Authenticator app</p>
          <p className="text-sm text-muted-foreground mt-1">
            After setup, login uses your authenticator by default. Email OTP stays available as backup.
          </p>
        </div>
        <Badge variant={totpEnabled ? 'default' : 'outline'}>
          {totpEnabled ? 'Enabled' : 'Not configured'}
        </Badge>
      </div>

      {mfaStatus.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading MFA status...
        </div>
      )}

      {mfaStatus.isError && (
        <p className="text-sm text-destructive">{ERROR.MFA.STATUS_FAILED}</p>
      )}

      {!totpEnabled ? (
        <Button onClick={handleStartSetup} disabled={totpSetup.isPending || mfaStatus.isLoading}>
          {totpSetup.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Set up authenticator
        </Button>
      ) : (
        <div className="flex flex-col gap-2">
          <Button variant="outline" onClick={() => { setReauthCode(''); setView('rotate'); }}>
            Generate new recovery codes
          </Button>
          <Button variant="destructive" onClick={() => { setPassword(''); setReauthCode(''); setView('disable'); }}>
            Disable authenticator
          </Button>
        </div>
      )}
    </div>
  );

  const renderSetup = () => (
    <form onSubmit={handleConfirmSetup} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Scan this QR code with Google Authenticator, Microsoft Authenticator, Authy, 1Password, or Apple Passwords.
        Then enter the current 6-digit code. MFA is not enabled until that code is verified.
      </p>
      {setup?.otpauthUri && (
        <div className="flex justify-center bg-white p-4 rounded-md">
          <QRCodeSVG value={setup.otpauthUri} size={180} />
        </div>
      )}
      {setup?.manualKey && (
        <div className="space-y-2">
          <Label>Manual key</Label>
          <div className="flex gap-2">
            <Input readOnly value={formatTotpManualKey(setup.manualKey)} className="font-mono tracking-wider" />
            <Button
              type="button"
              variant="outline"
              onClick={() => copyText(setup.manualKey, 'Manual key')}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="setup-code">6-digit code</Label>
        <Input
          id="setup-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          value={setupCode}
          onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="text-center tracking-widest text-lg"
          maxLength={6}
          required
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => setView('overview')}>
          Cancel
        </Button>
        <Button type="submit" disabled={totpConfirm.isPending || setupCode.length !== 6}>
          {totpConfirm.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Verify and enable
        </Button>
      </div>
    </form>
  );

  const renderRecovery = () => (
    <div className="space-y-4">
      <Alert>
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>Save these recovery codes</AlertTitle>
        <AlertDescription>
          Each code can be used once if you lose access to your authenticator. They will not be shown again.
        </AlertDescription>
      </Alert>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-muted/50 p-3 rounded-md">
        {recoveryCodes.map((recoveryCode) => (
          <div key={recoveryCode}>{recoveryCode}</div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => copyText(recoveryCodes.join('\n'), 'Recovery codes')}
        >
          <Copy className="mr-2 h-4 w-4" />
          Copy
        </Button>
        <Button type="button" variant="outline" onClick={() => downloadRecoveryCodes(recoveryCodes)}>
          <Download className="mr-2 h-4 w-4" />
          Download
        </Button>
      </div>
      <div className="flex justify-end">
        <Button onClick={() => setView('overview')}>I have saved these codes</Button>
      </div>
    </div>
  );

  const renderReauthForm = (
    description: string,
    onSubmit: (e: React.FormEvent) => void,
    pending: boolean,
    includePassword: boolean,
    submitLabel: string,
    allowRecoveryCode = false,
  ) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="text-sm text-muted-foreground">{description}</p>
      {includePassword && (
        <div className="space-y-2">
          <Label htmlFor="mfa-password">Password</Label>
          <div className="relative">
            <Input
              id="mfa-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword((current) => !current)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="mfa-reauth-code">
          {allowRecoveryCode ? 'Authenticator or recovery code' : 'Authenticator code'}
        </Label>
        <Input
          id="mfa-reauth-code"
          inputMode={allowRecoveryCode ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          value={reauthCode}
          onChange={(e) => setReauthCode(
            allowRecoveryCode
              ? e.target.value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 12).toUpperCase()
              : e.target.value.replace(/\D/g, '').slice(0, 6),
          )}
          className="text-center tracking-widest text-lg"
          maxLength={allowRecoveryCode ? 12 : 6}
          required
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => setView('overview')}>
          Cancel
        </Button>
        <Button type="submit" variant={includePassword ? 'destructive' : 'default'} disabled={pending}>
          {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  );

  const titleByView: Record<DialogView, string> = {
    overview: 'Security',
    setup: 'Set up authenticator',
    recovery: 'Your recovery codes',
    disable: 'Disable authenticator',
    rotate: 'New recovery codes',
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{titleByView[view]}</DialogTitle>
          <DialogDescription>
            Multi-factor authentication for your Orbius account.
          </DialogDescription>
        </DialogHeader>
        {view === 'overview' && renderOverview()}
        {view === 'setup' && renderSetup()}
        {view === 'recovery' && renderRecovery()}
        {view === 'disable' && renderReauthForm(
          'Enter your password and a current authenticator code or recovery code to disable MFA. Email OTP will be used at login again.',
          handleDisable,
          disableTotp.isPending,
          true,
          'Disable MFA',
          true,
        )}
        {view === 'rotate' && renderReauthForm(
          'Enter a current authenticator code to replace your recovery codes. Previous codes will stop working.',
          handleRotate,
          rotateRecovery.isPending,
          false,
          'Generate codes',
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MfaSettingsDialog;
